import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

export type RTIResult = {
  score: number;
  badge: string;
  emotionalSignal: string;
  insight: string;
  suggestedResponse: string;
};

export function analyzeMessage(message: string): RTIResult {
  const text = message.toLowerCase();
  const boosts: Array<[string, number]> = [
    ['fine', 15],
    ['whatever', 18],
    ['nothing', 12],
    ["don't care", 18],
    ['leave me alone', 22],
    ['always', 10],
    ['never', 10],
    ['forget it', 15],
    ['done', 14],
    ['tired of this', 20],
  ];
  const easers: Array<[string, number]> = [
    ['sorry', -18],
    ['understand', -12],
    ['love', -15],
    ['appreciate', -12],
    ['talk', -10],
    ['listen', -10],
    ['work through', -14],
    ['repair', -16],
    ['care', -10],
  ];

  let score = 50;
  for (const [token, delta] of boosts) {
    if (text.includes(token)) score += delta;
  }
  for (const [token, delta] of easers) {
    if (text.includes(token)) score += delta;
  }
  score = Math.max(8, Math.min(96, score));

  return {
    score,
    badge: getRTIBadge(score),
    emotionalSignal: getEmotionalSignal(score, message),
    insight:
      score >= 71
        ? 'This message may signal emotional distance, frustration, or a need to feel understood before the conversation can move forward safely.'
        : score >= 36
          ? 'There may be mixed emotion here. The message may benefit from a calmer follow-up before either person reacts too quickly.'
          : 'This message appears more open, repair-focused, and emotionally safer. It may create space for understanding.',
    suggestedResponse: getSuggestedResponse(score),
  };
}

export function getRTIBadge(score: number): string {
  if (score <= 35) return 'Low Tension';
  if (score <= 70) return 'Moderate Tension';
  return 'High Tension';
}

export function getSuggestedResponse(score: number): string {
  if (score >= 71) {
    return "I don't want to assume what you mean. I care about understanding you. Can we slow this down and talk calmly?";
  }
  if (score >= 36) {
    return "I hear you. I want to understand what you're feeling without making this worse.";
  }
  return "Thank you for saying that. I want to keep listening and work through this with you.";
}

export function getEmotionalSignal(score: number, message: string): string {
  const text = message.toLowerCase();
  if (score >= 71) {
    if (text.includes('fine') || text.includes('whatever')) return 'Guarded frustration';
    if (text.includes('leave me alone')) return 'Emotional withdrawal';
    return 'Escalating tension';
  }
  if (score >= 36) return 'Mixed emotional signal';
  return 'Open to repair';
}

type PricingRTIDemoProps = {
  onStartFree: () => void;
};

export function PricingRTIDemo({ onStartFree }: PricingRTIDemoProps) {
  const [message, setMessage] = useState("I'm fine, do whatever you want.");
  const [runId, setRunId] = useState(0);
  const result = useMemo(() => analyzeMessage(message), [message, runId]);
  const [animatedScore, setAnimatedScore] = useState(0);
  const ringValue = Math.max(0, Math.min(100, animatedScore));

  useEffect(() => {
    let frame = 0;
    const totalFrames = 24;
    const target = result.score;
    const timer = window.setInterval(() => {
      frame += 1;
      const next = Math.round((target * frame) / totalFrames);
      setAnimatedScore(next);
      if (frame >= totalFrames) window.clearInterval(timer);
    }, 16);
    return () => window.clearInterval(timer);
  }, [result.score]);

  return (
    <section className="relative mt-12 overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-neutral-950 via-purple-950/45 to-pink-950/35 p-6 shadow-[0_28px_80px_rgba(10,10,20,0.55)] backdrop-blur-xl sm:p-8">
      <div className="pointer-events-none absolute -left-16 top-16 h-44 w-44 rounded-full bg-purple-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-12 h-52 w-52 rounded-full bg-pink-400/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]" />
      <motion.div
        aria-hidden
        animate={{ y: [0, -6, 0], x: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute right-6 top-6 hidden rounded-full border border-pink-200/30 bg-pink-300/15 px-3 py-1 text-[11px] text-pink-100 lg:block"
      >
        Tone detected
      </motion.div>
      <motion.div
        aria-hidden
        animate={{ y: [0, -7, 0], x: [0, -4, 0] }}
        transition={{ duration: 6.8, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute left-8 top-24 hidden rounded-full border border-purple-200/30 bg-purple-300/15 px-3 py-1 text-[11px] text-purple-100 lg:block"
      >
        Guided response
      </motion.div>

      <h3 className="relative z-10 text-center text-2xl font-light text-white sm:text-3xl">Try TENSION in 30 seconds</h3>
      <p className="relative z-10 mx-auto mt-2 max-w-2xl text-center text-sm text-white/75">
        Type a real message, and TENSION will reveal the emotional signal underneath.
      </p>

      <div className="relative z-10 mx-auto mt-6 max-w-4xl rounded-3xl border border-white/20 bg-white/10 p-4 shadow-[0_16px_44px_rgba(10,10,30,0.45)] backdrop-blur-xl">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
          {['Tone detected', 'Real-time clarity', 'Guided response'].map((badge) => (
            <span key={badge} className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-white/80">
              {badge}
            </span>
          ))}
        </div>
        <label className="text-xs font-medium uppercase tracking-wide text-white/60">Interaction message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Example: I'm fine, do whatever you want."
          className="mt-2 h-28 w-full resize-none rounded-2xl border border-white/20 bg-black/30 px-4 py-3 text-sm text-white outline-none transition-all duration-300 ease-in-out placeholder:text-white/45 focus:border-pink-300/60 focus:ring-2 focus:ring-purple-400/30"
        />
        <div className="mt-3 flex justify-end">
          <Button
            onClick={() => setRunId((x) => x + 1)}
            className="rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_10px_24px_rgba(168,85,247,0.4)] transition-all duration-300 ease-in-out"
          >
            Analyze interaction
          </Button>
        </div>
      </div>

      <motion.div
        key={runId}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="relative z-10 mx-auto mt-6 max-w-4xl rounded-3xl border border-white/20 bg-white/10 p-5 shadow-[0_22px_56px_rgba(10,10,30,0.52)] backdrop-blur-xl"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/20 bg-black/25 p-4">
            <p className="text-xs uppercase tracking-wide text-white/60">RTI Score</p>
            <div className="mt-3 flex items-center gap-4">
              <div
                className="relative flex h-20 w-20 items-center justify-center rounded-full border border-white/15"
                style={{
                  background: `conic-gradient(rgba(236,72,153,0.95) ${ringValue * 3.6}deg, rgba(255,255,255,0.15) ${ringValue * 3.6}deg)`,
                }}
              >
                <div className="absolute inset-[7px] rounded-full bg-neutral-950/95" />
                <p className="relative z-10 text-xl font-light text-white">{animatedScore}</p>
              </div>
              <span className="rounded-full border border-purple-300/40 bg-purple-500/20 px-2.5 py-1 text-xs text-purple-100">
                {result.badge}
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/15">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${result.score}%` }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
                className="h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
              />
            </div>
            <div className="mt-4 flex items-end gap-1">
              {[18, 30, 22, 40, 28, 36, 26, 34].map((h, idx) => (
                <motion.span
                  key={`${h}-${idx}`}
                  initial={{ height: 4 }}
                  animate={{ height: h }}
                  transition={{ duration: 0.4, delay: idx * 0.03, ease: 'easeInOut' }}
                  className="w-2 rounded-full bg-gradient-to-t from-purple-500/70 to-pink-400/90"
                />
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/20 bg-black/25 p-4">
            <p className="text-xs uppercase tracking-wide text-white/60">Emotional Signal</p>
            <p className="mt-2 text-sm font-medium text-white">{result.emotionalSignal}</p>
            <p className="mt-3 text-xs uppercase tracking-wide text-white/60">What may be happening underneath</p>
            <p className="mt-3 text-xs text-white/80">{result.insight}</p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-black/25 p-4">
            <p className="text-xs uppercase tracking-wide text-white/60">Suggested Response</p>
            <p className="mt-2 text-sm leading-relaxed text-white/85">{result.suggestedResponse}</p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-black/25 p-4">
            <p className="text-xs uppercase tracking-wide text-white/60">What may be happening underneath</p>
            <p className="mt-2 text-sm text-white/80">
              The model detects subtle tension cues, maps likely intent shifts, and proposes a calmer response path before escalation.
            </p>
            <div className="mt-3 flex items-end gap-1">
              {[12, 18, 14, 22, 16, 20, 15, 19, 13].map((h, idx) => (
                <motion.span
                  key={`${h}-${idx}`}
                  className="w-1.5 rounded-full bg-gradient-to-t from-pink-400/75 to-purple-300/90"
                  animate={{ height: [Math.max(6, h - 5), h, Math.max(6, h - 3), h] }}
                  transition={{ duration: 2.1, repeat: Infinity, delay: idx * 0.06, ease: 'easeInOut' }}
                />
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/20 bg-black/25 p-4 sm:col-span-2">
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="inline-flex items-center gap-1 text-xs text-white/70">
                <Sparkles className="h-3.5 w-3.5 text-pink-300" />
                This is a preview. TENSION gives deeper real-time guidance inside the app.
              </p>
              <Button
                onClick={onStartFree}
                className="rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_10px_24px_rgba(168,85,247,0.4)] transition-all duration-300 ease-in-out"
              >
                Start free now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
